import { Component, ChangeDetectionStrategy, Input, Output, EventEmitter, OnChanges, SimpleChanges } from '@angular/core';
import { UntypedFormGroup, UntypedFormBuilder, Validators } from '@angular/forms';
import { Task, IndexCard, TaskComment, Client } from '@store/models';
import { NgbDateStruct, NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { AlertService } from 'app/core/alerts/alert.service';
import { ConfirmService } from 'app/shared/confirm-dialog/confirm-dialog.service';
import { LuxonUtils } from '@shared';

@Component({
    selector: 'app-task-ui',
    templateUrl: './task.component.html',
    styleUrls: ['./task.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})

export class TaskComponent implements OnChanges {

    today: NgbDateStruct = {
        year: LuxonUtils.now().year,
        month: LuxonUtils.now().month,
        day: LuxonUtils.now().day
    };
    form: UntypedFormGroup;
    edit = true;
    showComments = false;
    commenting = false;
    @Input() showDone: boolean;
    @Input() showReject: boolean;
    @Input() userCard: IndexCard;
    @Input() taskUsers: IndexCard[];
    @Input() allUsers: IndexCard[];
    @Input() task: Task;
    @Input() taskClient: Client;
    @Input() taskComments: TaskComment[];
    @Input() taskDescription: string;
    @Output() saveTask = new EventEmitter<Task>();
    @Output() saveTaskComment = new EventEmitter<Partial<TaskComment>>();

    get canEdit(): boolean {
        if (this.task && this.task.afsluttet) {
            return false;
        }
        if ((this.task && this.task.mid === this.userCard.cardId) || this.userCard.lev >= 4) {
            return true;
        }
        return false;
    }

    get canDone(): boolean {
        return this.task ? this.task.fup === this.userCard.cardId && !this.task.afsluttet : false;
    }

    get canDelete(): boolean {
        const userId = this.userCard.cardId;
        return this.task
            ? this.task.mid === userId
                || this.userCard.lev >= 4
                || (this.task.afsluttet !== null && userId === this.taskClient.kundeansvarlig)
            : false;
    }

    get sortedTaskComments(): TaskComment[] {
        return this.taskComments.sort((a, b) => new Date(a.created).getTime() - new Date(b.created).getTime());
    }

    get commentBase(): Partial<TaskComment> {
        return {
            taskId: this.task.lid,
            created: new Date(),
            creator: this.userCard.cardId,
        };
    }

    constructor(
        private activeModal: NgbActiveModal,
        private fb: UntypedFormBuilder,
        private alert: AlertService,
        private confirm: ConfirmService,
    ) {
        this.createTaskForm();
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes.task) {
            if (this.task) {
                this.form.patchValue(this.task);
                if (this.task.lid) {
                    this.onEditCancel();
                }
            }
        }
        if (changes.taskDescription) {
            this.form.patchValue({ beskrivelse: this.taskDescription });
        }
    }

    createTaskForm() {
        this.form = this.fb.group({
            tekst: ['', Validators.required],
            fup: 0,
            deadline: [null],
            beskrivelse: ''
        });
    }

    getUsername(mid: number): string {
        if (!this.allUsers) { return ''; }
        if (!mid && this.userCard && this.task?.lid === 0) { mid = this.userCard.cardId; }
        const user = this.allUsers.find(u => u.cardId === mid);
        return user ? user.navn : '';
    }

    commentHeader(comment: TaskComment): string {
        const creator = this.getUsername(comment.creator);
        let typeText: string;
        switch (comment.type) {
            case 'create': typeText = 'oprettede opgaven'; break;
            case 'comment': typeText = 'skrev'; break;
            case 'assign': typeText = `skiftede opgaveløser til ${comment.comment}`; break;
            case 'reject': typeText = 'afviste opgaven'; break;
            case 'done': typeText = 'afsluttede opgaven'; break;
            case 'delete': typeText = 'slettede opgaven'; break;
            default: break;
        }
        return `${creator} ${typeText}`;
    }

    onClose() {
        this.activeModal.dismiss();
    }

    onEdit() {
        this.form.enable();
        this.edit = true;
    }

    onEditCancel() {
        this.form.disable();
        this.edit = false;
        if (!this.task || !this.task.lid) {
            this.activeModal.close();
        }
    }

    onSave() {
        if (!this.form.valid) {
            this.alert.createAlert('Fejl i formular!', 'danger');
            return;
        }
        let task: Task;
        if (this.task && this.task.lid > 0) {
            const fup = this.form.get('fup').value;
            if (this.task.fup !== fup) {
                const fupUser = this.taskUsers.find(u => u.cardId === +fup);
                const fupName = fupUser ? fupUser.navn : '';
                this.saveTaskComment.emit({
                    ...this.commentBase,
                    type: 'assign',
                    comment: fupName
                });
            }
            task = {
                ...this.task,
                ...this.form.value
            };
            this.form.disable();
            this.edit = false;
        } else {
            task = {
                ...this.form.value,
                lid: 0,
                kid: this.task.kid,
                mid: null, // set in Effect
                kontor: null, // set in Effect
                dato: new Date(),
                afsluttet: null,
                slettet: null
            };
        }
        this.saveTask.emit(task);
    }

    onDone(doneText: string) {
        this.saveTaskComment.emit({
            ...this.commentBase,
            type: 'done',
            comment: doneText
        });
        const doneTask = {
            ...this.task,
            ...this.form.value,
            afsluttet: new Date(),
        };
        this.saveTask.emit(doneTask);
    }

    onReject(rejectText: string) {
        this.saveTaskComment.emit({
            ...this.commentBase,
            type: 'reject',
            comment: rejectText
        });
        const rejectTask = {
            ...this.task,
            ...this.form.value,
            fup: 0,
        };
        this.saveTask.emit(rejectTask);
    }

    newComment(commentText: string) {
        if (!commentText) {
            this.alert.createAlert('Udfyld kommentar feltet!', 'warning');
            return;
        }
        this.saveTaskComment.emit({
            ...this.commentBase,
            type: 'comment',
            comment: commentText
        });
        this.commenting = false;
    }

    onDelete() {
        this.confirm.question(`Er du sikker på du vil slette opgaven?`, 'Ja', 'Nej').then(confirmed => {
            if (confirmed) {
                this.deleteTask();
            }
        });
    }

    deleteTask() {
        this.saveTaskComment.emit({
            ...this.commentBase,
            type: 'delete'
        });
        const deleteTask = {
            ...this.task,
            ...this.form.value,
            slettet: new Date(),
        };
        this.saveTask.emit(deleteTask);
    }

}
